import React from "react";
import { Link } from "react-router-dom";

export default function About() {
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="about-page">
      <div className="text-center mb-12">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a34a] mb-3">Our Story</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#2b1b1c]">Sarees by Raunak</h1>
        <div className="gold-divider mt-6"><span className="text-lg">✦</span></div>
        <p className="font-italic-serif text-xl text-[#7a1e2b] mt-6">Woven in Tradition. Chosen for Today.</p>
      </div>

      <div className="prose-story text-[#2b1b1c] leading-relaxed space-y-6">
        <p>
          At <strong>Sarees by Raunak</strong>, we believe a saree is more than six yards of fabric.
          It is an expression of identity, a celebration of culture, and a story passed from one generation to another.
        </p>
        <p>
          Our journey began with a simple love for sarees and a desire to bring the beauty of India&rsquo;s diverse textile traditions
          closer to women everywhere. From the simplicity of everyday cotton to the grandeur of exquisite silk, every saree has
          a story to tell&mdash;and we want to help you find yours.
        </p>

        <SectionHeading>Celebrating India&rsquo;s Saree Heritage</SectionHeading>
        <p>
          India is home to an extraordinary variety of sarees, each shaped by the traditions, artistry, landscapes, and craftsmanship of its region.
        </p>
        <p>
          Our collections are inspired by this incredible diversity&mdash;from <em>Kanjeevaram</em> and <em>Banarasi</em> silks to
          <em> Chanderi, Maheshwari, Paithani, Patola, Kota Doria, Tant, Jamdani, Sambalpuri, Tussar, Chikankari</em> and
          <em> Mangalgiri</em> cottons.
        </p>
        <p>
          We also celebrate the beauty of regional weaves and crafts such as <em>Ikat, Kalamkari, Ajrakh, Bagru, Bandhani</em> and
          <em> Leheriya</em>, bringing together India&rsquo;s rich textile vocabulary in thoughtfully curated collections.
        </p>
        <p>
          Whether you love the understated elegance of a handwoven cotton saree, the intricate beauty of a traditional weave, or
          the luxurious charm of silk, our aim is to help you discover sarees that feel authentic, beautiful, and uniquely yours.
        </p>

        <SectionHeading>Tradition Meets Modern Living</SectionHeading>
        <p>We understand that the saree continues to evolve with the woman who wears it.</p>
        <p>
          Today&rsquo;s woman may wear a lightweight cotton saree to work, a graceful handloom saree for a family gathering, a
          statement silk for a wedding, or a contemporary drape simply because she wants to feel beautiful.
        </p>
        <p>
          That is why we curate sarees for everyday moments, celebrations, festivals, workwear, weddings, gifting, and everything in between.
        </p>
        <p>
          Our collections bring together traditional craftsmanship and contemporary sensibilities&mdash;beautiful colors, thoughtful designs,
          comfortable fabrics, and styles that make the timeless saree effortless to wear.
        </p>

        <SectionHeading>More Than a Saree</SectionHeading>
        <p>At Sarees by Raunak, we believe that choosing a saree should be an experience.</p>
        <p>
          We carefully consider fabric, craftsmanship, colors, motifs, comfort, versatility, and value when bringing a saree to our collection.
          We want every saree you purchase to be one that you look forward to wearing&mdash;and one that becomes part of a memory.
        </p>
        <ul className="border-l-2 border-[#c9a34a] pl-5 space-y-2 font-italic-serif text-[#7a1e2b] text-lg">
          <li>A saree gifted by a daughter to her mother.</li>
          <li>A saree chosen for a first festival after marriage.</li>
          <li>A saree worn to an important celebration.</li>
          <li>A saree bought simply because the color felt perfect.</li>
        </ul>
        <p>These are the moments that make a saree special.</p>

        <SectionHeading>Our Promise</SectionHeading>
        <p>We are committed to offering:</p>
        <ul className="space-y-4">
          <Promise title="Carefully curated collections">Sarees inspired by India&rsquo;s diverse weaving traditions and contemporary styles.</Promise>
          <Promise title="Quality you can trust">We pay close attention to fabric, finishing, craftsmanship, and overall quality.</Promise>
          <Promise title="Timeless elegance">Sarees that celebrate Indian heritage while fitting beautifully into modern wardrobes.</Promise>
          <Promise title="Honest value">Beautiful sarees at thoughtfully considered prices.</Promise>
          <Promise title="A personal experience">Because we believe shopping for a saree should feel as special as wearing one.</Promise>
          <Promise title="Delivery across India">We bring our collection to saree lovers across the country.</Promise>
        </ul>

        <SectionHeading>Our Vision</SectionHeading>
        <p>Our vision is to create more than just a saree store.</p>
        <p>
          We want Sarees by Raunak to become a celebration of India&rsquo;s textile heritage&mdash;a place where the beauty of regional weaves,
          traditional craftsmanship, contemporary design, and the personal stories of the women who wear them come together.
        </p>
        <p>
          From the looms of Kanchipuram to the weavers of Banaras, from the colors of Rajasthan and Gujarat to the cotton traditions of
          South India, from Bengal&rsquo;s delicate weaves to Maharashtra&rsquo;s iconic Paithani, India&rsquo;s saree heritage is wonderfully diverse.
        </p>
        <p>And this is only the beginning of our journey.</p>
        <p>We invite you to discover that heritage with us, one saree at a time.</p>
      </div>

      {/* Sign-off */}
      <div className="mt-14 text-center">
        <div className="gold-divider"><span className="text-lg">✦</span></div>
        <div className="font-serif text-2xl text-[#2b1b1c] mt-6">Sarees by Raunak</div>
        <div className="font-italic-serif text-[#7a1e2b] mt-1">Draping traditions. Creating memories.</div>
        <Link to="/shop" className="btn-primary inline-block mt-8" data-testid="about-cta-shop">Explore our collection</Link>
      </div>
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <h2 className="font-serif text-2xl text-[#2b1b1c] mt-10 pt-4">
      <span className="text-[#c9a34a] mr-2">✦</span>{children}
    </h2>
  );
}

function Promise({ title, children }) {
  return (
    <li className="flex gap-3">
      <span className="text-[#c9a34a] mt-1 flex-shrink-0">✨</span>
      <span>
        <strong className="font-serif text-[#2b1b1c]">{title}</strong>
        <span className="block text-[#2b1b1c]">{children}</span>
      </span>
    </li>
  );
}
